import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  FileText,
  Loader2,
  Lock,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import {
  FieldRow,
  TextInput,
  TextArea,
  SelectField,
  CheckboxField,
} from "../../components/portal/create/fields";
import { TICKET_CURRENCIES } from "../../components/portal/create/wizardConstants";
import { useAuth } from "../../lib/auth";
import { parseApiError } from "../../lib/api";
import { COUNTRIES } from "../../lib/constants";
import { cn } from "../../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const UPDATED_AT = "Last updated: 3 July 2026";
const ORG_FIELD_MAX = 50;
const INSURANCE_FIELD_MAX = 30;
const PHONE_DIGIT_MAX = 15;
const PHONE_PATTERN = /^\+\d{7,15}$/;

const toLocalDateString = (date) => date.toLocaleDateString("en-CA");
const addYears = (base, years) => {
  const next = new Date(base);
  next.setFullYear(next.getFullYear() + years);
  return next;
};
const INSURANCE_START_MIN = toLocalDateString(addYears(new Date(), -5));
const INSURANCE_START_MAX = toLocalDateString(addYears(new Date(), 1));
const INSURANCE_END_MIN = toLocalDateString(new Date());
const INSURANCE_END_MAX = toLocalDateString(addYears(new Date(), 5));

const normalisePhoneInput = (value) =>
  `+${String(value || "").replace(/\D/g, "").slice(0, PHONE_DIGIT_MAX)}`;
const normalisePersonNameInput = (value) => String(value || "").replace(/\d/g, "");
const limitText = (value, max) => String(value || "").slice(0, max);
const clampDateString = (value, min, max) => {
  const next = String(value || "").slice(0, 10);
  if (!next) return "";
  if (min && next < min) return min;
  if (max && next > max) return max;
  return next;
};

const LEGAL_ERROR_MESSAGES = {
  get legal_name() { return tr("Enter the legal organization name"); },
  get identity_registration_proof() { return tr("Upload identity or registration proof"); },
  get identity_document_type() { return tr("Please describe the document type in 60 characters or less"); },
  get country() { return tr("Select a country"); },
  get city() { return tr("Enter a city"); },
  get address() { return tr("Enter an address"); },
  get primary_contact_full_name() { return tr("Enter a contact name without numbers"); },
  get primary_contact_email() { return tr("Enter a valid email address"); },
  get primary_contact_phone() { return tr("Enter a phone number starting with + followed by 7 to 15 digits"); },
  get emergency_contact_phone() { return tr("Enter a phone number starting with + followed by 7 to 15 digits"); },
  get insurance_provider_name() { return tr("Enter the insurance provider"); },
  get insurance_policy_number() { return tr("Enter the policy number"); },
  get insurance_coverage_currency() { return tr("Select a coverage currency"); },
  get insurance_coverage_amount() { return tr("Set a coverage amount greater than 0"); },
  get insurance_coverage_start_date() { return tr("Select the coverage start date"); },
  get insurance_coverage_end_date() { return tr("End date must be on or after the start date"); },
  get insurance_document() { return tr("Upload the insurance document"); },
  get supplier_terms_document() { return tr("Upload your organization's terms & conditions"); },
  get supplier_privacy_document() { return tr("Upload your organization's privacy policy"); },
  get confirm_accuracy() { return tr("Please confirm before submitting"); },
  get acknowledge_voocly_terms() { return tr("Please acknowledge before submitting"); },
};

const LEGAL_DOCUMENT_TABS = [
  {
    key: "terms",
    get label() { return tr("Terms & conditions"); },
    icon: FileText,
    reviewed: true,
    get intro() { return tr("These Terms and Conditions govern the use of the Voocly website, mobile applications and related services. By accessing, browsing, registering with, or making a booking through the Platform, you agree to be legally bound by these Terms."); },
    sections: [
      {
        get title() { return tr("1. Introduction"); },
        get body() { return tr("If you do not accept these Terms, you must not use the Platform or make any bookings through it."); },
      },
      {
        get title() { return tr("2. Role of Voocly and structure of the contract"); },
        body: [
          "Voocly operates an online marketplace that enables users to discover, book and review tours, activities, tickets and other related experiences provided by independent third-party suppliers (Partners). When you book an experience via Voocly, you enter into a direct contract for the provision of services with the relevant Partner; Voocly is not a party to that service contract and acts only as an intermediary or commercial agent where permitted by law.",
          "Voocly is responsible for operating the Platform, processing bookings on behalf of partners and, where applicable, collecting payments from you as commercial agent, but Voocly does not itself provide or operate the tours, activities or experiences. The Partner is solely responsible for delivering the booked services in accordance with the description, applicable laws and industry standards.",
        ],
      },
      {
        get title() { return tr("3. Eligibility and user obligations"); },
        body: [
          "You may use the Platform and make bookings only if you are at least 18 years of age and/or have the legal capacity to enter into binding contracts. By making a booking, you represent and warrant that all information you provide is true, accurate, current and complete.",
          "You are responsible for maintaining the confidentiality of your account credentials and for all activities carried out under your account. You agree to notify Voocly immediately of any unauthorised access to or use of your account or any breach of security.",
        ],
      },
      {
        get title() { return tr("4. Account registration"); },
        body: [
          "Certain features of the Platform require you to create a user account by providing your name, email address, password and other required information. Voocly may refuse registration or suspend or terminate accounts at its reasonable discretion if it considers that these Terms or applicable laws have been or may be breached.",
          "You must keep your account details up to date and promptly update any changes to personal or contact information.",
        ],
      },
      {
        get title() { return tr("5. Booking process and contract formation"); },
        body: [
          "The Platform displays experiences and services as made available by Partners, including description, inclusions, exclusions, prices, availability, meeting points, images, and key conditions. When you select a product and complete the booking process by providing required details and payment information, you submit a binding offer to purchase the services from the Partner.",
          "The contract between you and the Partner is formed when you receive a booking confirmation issued on behalf of the Partner; Voocly will provide confirmation via email, in-app notification or other agreed means. You are responsible for reviewing the confirmation and contacting Voocly promptly if there are obvious errors or discrepancies.",
        ],
      },
      {
        get title() { return tr("6. Prices, fees and taxes"); },
        body: [
          "Prices displayed on the Platform are set by Partners and are generally shown per person or per group, as specified, and in the applicable currency. Unless indicated otherwise, prices include mandatory fees essential to participate in the core itinerary, but may exclude optional extras, personal expenses and government-imposed charges.",
          "Partners may change prices at any time before you place a booking; once your booking is confirmed, the agreed price will apply except where a clear, technical pricing error is evident. Taxes, fees and charges may differ by destination and product and are the responsibility of the Partner unless expressly stated otherwise.",
        ],
      },
      {
        get title() { return tr("7. Payments"); },
        body: [
          "Voocly, acting as commercial agent, may collect payment for bookings on behalf of Partners via the payment methods available on the Platform. You authorise Voocly or its payment service providers to charge your selected payment method for the total price displayed during booking, including applicable taxes and fees.",
          "Payments are processed through secure third-party payment processors; Voocly does not store full payment card numbers and uses appropriate measures to reduce fraud risk. If payment cannot be processed, your booking may be cancelled or not confirmed.",
        ],
      },
      {
        get title() { return tr("8. Cancellations, changes and refunds"); },
        body: [
          "Customer cancellations and changes: cancellation and change policies are set by the Partner and are shown during booking and in your confirmation. Common policies include standard cancellation, all sales final, and custom policies defined by the Partner and presented before purchase.",
          "To cancel or change a booking, you must follow the procedures indicated in your confirmation or on the Platform; requests are only effective when received by Voocly or the Partner within the applicable policy period. If you fail to appear or arrive late beyond the time permitted under the policy, the booking may be treated as used and no refund will be due.",
          "Partner cancellations and amendments: Partners may cancel or significantly modify a booking due to operational reasons, low participation, safety concerns or events beyond their reasonable control, including force majeure events. In such cases, you will be offered one or more of the following: an alternative date or product, a voucher or a refund in accordance with the applicable policy and law.",
          "Voocly will reasonably assist in communicating and facilitating any such remedies but is not responsible for additional costs incurred by you, such as travel or accommodation, unless required by mandatory law.",
        ],
      },
      {
        get title() { return tr("9. Travel requirements and participant responsibilities"); },
        body: [
          "You are solely responsible for ensuring that you and all participants in your booking meet all entry, visa, passport, health and other requirements for the country or region where the experience takes place. Voocly and Partners do not assume liability for denied entry, missed services or additional costs resulting from your failure to meet such requirements.",
          "You must comply with all instructions provided in your booking confirmation, including meeting point, start time, dress code, equipment requirements and conduct rules. Partners may refuse participation or remove participants who behave in a manner that is unsafe, illegal, disruptive or inconsistent with reasonable expectations, without any entitlement to refund.",
        ],
      },
      {
        get title() { return tr("10. Platform content and intellectual property"); },
        body: [
          "All content on the Platform, including text, photos, videos, graphics, logos, trademarks, design and software, is owned by Voocly, Partners or their licensors and is protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable licence to access and use the Platform for personal, non-commercial purposes in accordance with these Terms.",
          "You may not copy, modify, reproduce, distribute, publicly display, publish, license, create derivative works from, or exploit any content without prior written permission from Voocly or the relevant rights holder. Any unauthorised use may result in termination of your account and legal action.",
        ],
      },
      {
        get title() { return tr("11. User-generated content"); },
        body: [
          "The Platform may allow you to submit reviews, ratings, photos, videos, questions, messages and other content (User Content). By submitting User Content, you represent and warrant that you have all necessary rights to do so and that your content does not infringe any third-party rights, violate laws or contain unlawful, offensive or misleading material.",
          "You grant Voocly and its affiliates a non-exclusive, worldwide, royalty-free, sublicensable licence to use, reproduce, adapt, translate, modify, create derivative works from, publicly display and distribute your User Content in connection with the Platform, marketing materials and related services. Voocly may, but is not obliged to, monitor, edit or remove User Content at its discretion.",
        ],
      },
      {
        get title() { return tr("12. Prohibited activities"); },
        body: [
          "You agree not to use the Platform or any content for commercial purposes not expressly authorised by Voocly.",
          "You agree not to make speculative, false or fraudulent bookings.",
          "You agree not to circumvent or attempt to circumvent Voocly by contacting Partners directly to avoid service or platform fees for products discovered or booked through Voocly, where such conduct is restricted.",
          "You agree not to interfere with or disrupt the operation of the Platform, including by uploading malware, automated scripts, scraping or excessive load.",
          "You agree not to violate any applicable laws, regulations or third-party rights in connection with your use of the Platform.",
          "Voocly reserves the right to suspend or terminate accounts and bookings where it reasonably believes that prohibited activities or breaches of these Terms have occurred.",
        ],
      },
      {
        get title() { return tr("13. Disclaimer and limitation of liability"); },
        body: [
          "The Platform and all displayed content and services are provided as is and as available without any warranties of quality, fitness for a particular purpose or non-infringement, except where such warranties cannot be excluded under mandatory law. Voocly does not guarantee that the Platform will be uninterrupted, error-free or free from unauthorised changes by third parties.",
          "Voocly's responsibility is limited to the operation of the Platform and the processing of bookings as commercial agent, and Voocly is not liable for the acts, omissions, errors, representations, warranties, breaches or negligence of partners or any third parties. To the maximum extent permitted by law, Voocly's aggregate liability arising out of or in connection with your use of the Platform and bookings made through it is limited to the total amount paid by you to Voocly for the booking giving rise to the claim.",
          "Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or for any other liability that cannot be excluded or limited under applicable law.",
        ],
      },
      {
        get title() { return tr("14. Indemnification"); },
        body: [
          "You agree to indemnify and hold harmless Voocly, its affiliates, officers, directors, employees and agents from and against all claims, demands, actions, damages, losses, costs and expenses, including reasonable legal fees, arising out of or in connection with your breach of these Terms or applicable laws.",
          "This indemnity also applies to your use of the Platform and any booking made through it, and to your User Content or infringement of any third-party rights.",
        ],
      },
      {
        get title() { return tr("15. Force majeure"); },
        body: [
          "Neither Voocly nor Partners will be liable for failure to perform or delays in performing obligations where such failure or delay is due to events beyond their reasonable control, including but not limited to natural disasters, extreme weather, acts of government, war, terrorism, civil unrest, epidemics, strikes, or failures of telecommunications or transport infrastructure (Force Majeure Events).",
          "In the event of a Force Majeure Event affecting your booking, the Partner's and Voocly's obligations will be suspended for the duration of the event, and remedies such as rebooking, vouchers or refunds will be provided where required by policy and law.",
        ],
      },
      {
        get title() { return tr("16. Third-party links and services"); },
        body: [
          "The Platform may contain links to third-party websites, applications or services that are not owned or controlled by Voocly. Voocly does not endorse or assume responsibility for the content, privacy policies or practices of such third parties.",
          "Your use of third-party services is at your own risk and subject to the terms and conditions of those third parties.",
        ],
      },
      {
        get title() { return tr("17. Privacy"); },
        get body() { return tr("Your use of the Platform is also governed by Voocly's Privacy Policy, which describes how personal data is collected, used, shared and protected. The Privacy Policy is incorporated into these Terms by reference, and you should read it carefully."); },
      },
      {
        get title() { return tr("18. Changes to the Terms"); },
        body: [
          "Voocly may modify these Terms from time to time to reflect changes in legal requirements, Platform functionality or business practices. Updated Terms will be posted on the Platform with an updated Last updated date and, where required by law, notified to you via email or in-app message.",
          "If you continue to use the Platform or make bookings after the updated Terms take effect, you will be deemed to have accepted the changes.",
        ],
      },
      {
        get title() { return tr("19. Termination"); },
        body: [
          "Voocly may terminate or suspend your access to the Platform and/or any booking, in whole or in part, at any time and with immediate effect if you materially breach these Terms or if Voocly reasonably suspects fraud, misuse or unlawful activity. You may terminate your account at any time by following the instructions provided on the Platform or contacting Voocly.",
          "Termination does not affect any existing bookings already confirmed; such bookings will remain subject to the Partner's terms and applicable cancellation policies, unless otherwise agreed or required by law. Clauses relating to limitation of liability, indemnification, intellectual property and dispute resolution will survive termination.",
        ],
      },
      {
        get title() { return tr("20. Miscellaneous"); },
        body: [
          "If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect. No failure or delay by Voocly in exercising any right or remedy under these Terms shall constitute a waiver of that or any other right or remedy.",
          "These Terms constitute the entire agreement between you and Voocly regarding the subject matter and supersede all prior or contemporaneous communications, whether oral or written, relating to the Platform and bookings made through it.",
        ],
      },
    ],
  },
  {
    key: "privacy",
    get label() { return tr("Privacy policy"); },
    icon: ShieldCheck,
    reviewed: true,
    get intro() { return tr("This Privacy Policy explains how Voocly collects, uses, discloses and protects personal data of individuals who use our website, mobile applications, and related services. It also describes the rights you have in relation to your personal data and how you can exercise those rights."); },
    sections: [
      {
        get title() { return tr("Data controller and contact details"); },
        get body() { return tr("Voocly acts as the data controller for personal data processed in connection with the provision of the Services, unless stated otherwise in this Privacy Policy."); },
      },
      {
        get title() { return tr("Scope and applicability"); },
        body: [
          "This Privacy Policy applies to visitors to our website and users of our mobile applications.",
          "It applies to customers who browse, book, or purchase travel experiences, tours, activities or related services through Voocly.",
          "It also applies to supply partners, tour operators, hosts and other business partners who list or provide services via Voocly.",
          "This Privacy Policy does not apply to third-party websites, platforms, payment processors or service providers that are not controlled by Voocly; their own privacy policies will govern how they process your personal data.",
        ],
      },
      {
        get title() { return tr("Categories of personal data we collect"); },
        body: [
          "Account and identity data: name, surname, username or other identifiers; contact details such as email address, phone number and postal address; passwords, authentication and security credentials.",
          "Booking and transaction data: details of bookings, reservations and purchases; payment-related information such as masked card details and payment status processed via third-party payment processors; invoices, receipts and tax-related details where required by law. Voocly does not store full card numbers.",
          "Travel and preference data: destination preferences, interests, languages, accessibility needs, special requests or notes you provide in relation to a booking.",
          "Communications and content: messages, emails and other communications exchanged with us or with suppliers via our platform; reviews, ratings, comments, photos or other content you submit.",
          "Usage and technical data: IP address, device identifiers, browser type, operating system, referral URLs, pages viewed and clickstream data; location data depending on your device settings and permissions; cookies and similar technologies.",
          "Supplier and business partner data: business contact information, company details, financial and tax information necessary for payments and invoicing.",
          "Legal and compliance data: records required to comply with legal obligations, including identity verification, invoices, contracts, claims, disputes, or law enforcement requests where applicable.",
        ],
      },
      {
        get title() { return tr("Sources of personal data"); },
        body: [
          "We obtain personal data directly from you when you create an account, make a booking, contact us, submit content, or otherwise interact with the Services.",
          "We obtain personal data automatically from your device and browser when you access the Services, including through cookies, logs and analytics tools.",
          "We may receive personal data from supply partners, tour operators, business partners and affiliates who facilitate bookings or provide services through Voocly.",
          "We may receive personal data from third-party service providers, such as payment processors, fraud prevention tools, and marketing platforms, as permitted by law.",
        ],
      },
      {
        get title() { return tr("Purposes and legal bases for processing"); },
        body: [
          "We process personal data only where we have a valid legal basis under applicable data protection laws, including the EU General Data Protection Regulation (GDPR) and similar laws.",
          "Provision of services and performance of contract: we process personal data to allow account management, facilitate browsing and booking, communicate booking confirmations, reminders, cancellations and updates, provide support, and handle inquiries, complaints and disputes.",
          "Legitimate interests: we process personal data where necessary to improve and develop the Services, protect our platform, users and suppliers against fraud, abuse and security threats, manage supplier and business partner relationships, and conduct aggregated analytics and business reporting where data is appropriately pseudonymised or anonymised.",
          "Compliance with legal obligations: we may process personal data to comply with legal, regulatory, tax and accounting obligations, including retention of booking and transaction records, responding to lawful requests and enforcing our rights.",
          "Consent: where required by law, we rely on consent to send certain marketing communications, use certain cookies and similar technologies for analytics and advertising, process sensitive categories of data if any, and process precise location data where applicable. You may withdraw consent at any time.",
        ],
      },
      {
        get title() { return tr("Cookies and tracking technologies"); },
        body: [
          "We use cookies, SDKs, web beacons and similar technologies to enable core functionality, analyse usage and support marketing and advertising.",
          "Strictly necessary cookies are required for site functions such as logging in, managing sessions and processing bookings.",
          "Functional cookies remember your preferences, such as language and currency, and enhance usability.",
          "Analytics cookies help us understand how users interact with the Services so we can improve performance and content.",
          "Advertising cookies are used to deliver relevant advertising and measure the effectiveness of campaigns, sometimes through third-party partners.",
          "Where required by law, we will request your consent before placing non-essential cookies on your device. You may manage cookie preferences through your browser or device settings, though disabling certain cookies may affect functionality.",
        ],
      },
      {
        get title() { return tr("Marketing communications"); },
        body: [
          "We may use your contact details and preference data to send marketing communications about experiences, tours, offers and services available via Voocly, as permitted by law.",
          "You can opt out of marketing communications at any time by clicking the unsubscribe link in the email or SMS, adjusting notification settings in your account or app, or contacting us via the Contact us page.",
          "Even if you opt out of marketing, we may still send service-related communications such as booking confirmations, important updates or legal notices.",
        ],
      },
      {
        get title() { return tr("Sharing of personal data"); },
        body: [
          "When you make a booking, we share relevant booking details with the supply partner or tour operator to fulfil your reservation.",
          "We engage third-party service providers to support operations, including payment processors, financial institutions, cloud hosting and IT infrastructure providers, analytics, advertising and marketing platforms, customer support tools and communication services.",
          "These providers are only permitted to process your personal data on our behalf and in accordance with our instructions.",
          "We may share limited data with business partners and affiliates for joint offerings, referral programs or distribution agreements, subject to appropriate legal bases and agreements.",
          "We may disclose personal data where required to comply with legal obligations, respond to lawful requests, protect our rights, users or suppliers, or in connection with legal proceedings, fraud prevention or security incidents.",
          "In the event of a merger, acquisition, reorganisation or sale of assets, personal data may be transferred as part of the transaction, subject to continued protection consistent with this Privacy Policy.",
          "We do not sell your personal data in the sense of transferring it for monetary consideration for independent use by third parties, as restricted under certain data protection laws.",
        ],
      },
      {
        get title() { return tr("International data transfers"); },
        body: [
          "Voocly may store and process personal data in countries outside your country of residence, including countries outside the European Economic Area (EEA), the United Kingdom, or the United States of America.",
          "Where such transfers occur, we implement appropriate safeguards required by law, such as relying on adequacy decisions, using standard contractual clauses and equivalent mechanisms, and applying technical and organisational measures to protect data during transfer.",
          "You may contact us for further information on the specific safeguards applied to international transfers relevant to your data.",
        ],
      },
      {
        get title() { return tr("Data retention"); },
        body: [
          "We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, or for longer where required or permitted by law, such as tax, accounting or legal obligations.",
          "Booking and transaction data are typically retained for the duration of your relationship with us and for a period thereafter as required by applicable laws, for example up to 6 years for financial records in certain jurisdictions.",
          "Account data is retained while your account remains active; if you close your account, we may retain limited data for legitimate business and legal purposes.",
          "Communications and content, such as reviews, may be retained as long as they remain relevant to the Services, subject to your rights.",
          "Supplier and business partner data is retained for the duration of the business relationship and thereafter as necessary to comply with legal obligations and manage potential claims.",
          "When personal data is no longer required, we will delete or irreversibly anonymise it in accordance with our data retention policy.",
        ],
      },
      {
        get title() { return tr("Data security"); },
        body: [
          "We implement appropriate technical and organisational measures to protect personal data against accidental or unlawful destruction, loss, alteration, unauthorised disclosure or access.",
          "These measures may include encryption in transit and at rest where appropriate, access controls and authentication mechanisms, regular security assessments and monitoring, staff training and internal policies on data protection.",
          "No method of transmission or storage is completely secure; you should also take steps to protect your account, such as using strong passwords and keeping your login details confidential.",
        ],
      },
      {
        get title() { return tr("Your data protection rights"); },
        body: [
          "Depending on your location and applicable law, you may have rights of access, rectification, erasure, restriction of processing, data portability, objection, and withdrawal of consent.",
          "To exercise your rights, please contact us using the Contact us page. We may need to verify your identity before fulfilling your request and may be legally entitled to refuse certain requests in defined circumstances.",
          "You also have the right to lodge a complaint with a competent data protection authority in your country of residence, place of work or place of alleged infringement.",
        ],
      },
      {
        get title() { return tr("Children's privacy"); },
        get body() { return tr("The Services are not intended for children under the age of 16, and we do not knowingly collect personal data from children under this age without appropriate parental consent where required by law. If we become aware that we have collected personal data from a child contrary to this provision, we will delete such data as soon as reasonably practicable."); },
      },
      {
        get title() { return tr("Changes to this Privacy Policy"); },
        get body() { return tr("We may update this Privacy Policy from time to time, for example to reflect changes in our practices, legal requirements or the Services. When we make material changes, we will take appropriate measures to inform you, such as updating the Last updated date at the top of this document, posting a notice on our website or notifying you by email or in-app message where required. Your continued use of the Services after the effective date of the updated Privacy Policy will constitute your acknowledgement of the changes."); },
      },
    ],
  },
];

const toSelectOptions = (items) => items.map((item) => ({ value: item, label: item }));

const COUNTRY_NAME_ALIASES = {
  "Antigua and Barbuda": "Antigua And Barbuda",
  Bahamas: "The Bahamas",
  "Cabo Verde": "Cape Verde",
  "Cote d'Ivoire": "Cote D'Ivoire (Ivory Coast)",
  Eswatini: "Swaziland",
  Fiji: "Fiji Islands",
  Gambia: "The Gambia",
  "North Macedonia": "Macedonia",
  Palestine: "Palestinian Territory Occupied",
  "Papua New Guinea": "Papua new Guinea",
  "Saint Kitts and Nevis": "Saint Kitts And Nevis",
  "Saint Vincent and the Grenadines": "Saint Vincent And The Grenadines",
  "Timor-Leste": "East Timor",
  "Trinidad and Tobago": "Trinidad And Tobago",
  "Vatican City": "Vatican City State (Holy See)",
};

const CITY_NAME_REWRITES = {
  "Abu Dhabi Island and Internal Islands": "Abu Dhabi",
  "Al Fujairah": "Fujairah",
  "Umm AL Quwain": "Umm Al Quwain",
};

let locationIndexPromise;

const loadLocationIndex = () => {
  if (!locationIndexPromise) {
    locationIndexPromise = import("country-state-city").then(({ City, Country }) => ({
      City,
      countryIsoByName: new Map(
        Country.getAllCountries().map((country) => [country.name, country.isoCode]),
      ),
    }));
  }
  return locationIndexPromise;
};

const getCountryIsoCode = (countryName, locationIndex) =>
  locationIndex?.countryIsoByName.get(COUNTRY_NAME_ALIASES[countryName] || countryName);

const cleanCityName = (cityName) => {
  const withoutAdminSuffix = String(cityName || "")
    .replace(/\s+(City|Municipality|County)$/i, "")
    .trim();
  return CITY_NAME_REWRITES[withoutAdminSuffix] || withoutAdminSuffix;
};

const getCityNamesForCountry = (countryName, locationIndex) => {
  const isoCode = getCountryIsoCode(countryName, locationIndex);
  if (!isoCode) return [];
  return [
    ...new Set(
      locationIndex.City.getCitiesOfCountry(isoCode)
        .map((city) => cleanCityName(city.name))
        .filter(Boolean),
    ),
  ].sort((a, b) => a.localeCompare(b));
};

const ACCEPTED_DOC_MIMES = ["application/pdf", "image/jpeg", "image/png"];
const ACCEPTED_DOC_LABEL = "PDF, JPG or PNG · up to 7 MB";
const MAX_DOC_BYTES = 7 * 1024 * 1024;

const IDENTITY_PROOF_HELPERS = {
  "registered-company":
    "Upload your Certificate of Incorporation or Company Registration Certificate.",
  "certified-individual":
    "Upload your freelancer registration certificate, trade license, or equivalent official document.",
  "uncertified-individual":
    "Upload a valid passport, national ID card, or driver's license.",
  "non-profit":
    "Upload your charity registration or official non-profit recognition document issued by a government authority.",
  "non profit":
    "Upload your charity registration or official non-profit recognition document issued by a government authority.",
  government:
    "Upload an official government letterhead or gazette notification confirming your entity's authority to operate.",
  "government entity":
    "Upload an official government letterhead or gazette notification confirming your entity's authority to operate.",
  educational:
    "Upload your ministry-issued accreditation certificate or official school/university registration document, or equivalent official document.",
  "educational institution":
    "Upload your ministry-issued accreditation certificate or official school/university registration document, or equivalent official document.",
  "student-club":
    "Upload a signed letter on your university's letterhead confirming your club's official registration and affiliation.",
  "student club":
    "Upload a signed letter on your university's letterhead confirming your club's official registration and affiliation.",
  other:
    "Upload any official document that proves your organization's legal existence or your authority to represent it.",
};

const identityProofHelper = (accountType) =>
  IDENTITY_PROOF_HELPERS[String(accountType || "").toLowerCase()] ||
  IDENTITY_PROOF_HELPERS.other;

const downloadDocument = (url, filename) => {
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/* ---- A reusable PDF/Image upload control ---- */
const DocumentUpload = ({ value, fileName, assetType, onChange, testid, label = "Upload" }) => {
  usePartnerTranslation();
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onPick = async (file) => {
    if (!file) return;
    if (!ACCEPTED_DOC_MIMES.includes(file.type)) {
      toast.error(tr("Unsupported file type"), { description: ACCEPTED_DOC_LABEL });
      return;
    }
    if (file.size > MAX_DOC_BYTES) {
      toast.error(tr("File too large"), { description: ACCEPTED_DOC_LABEL });
      return;
    }
    setUploading(true);
    try {
      const presign = await axios.post(`${API}/media/presign`, {
        asset_type: assetType,
        file_name: file.name,
        mime_type: file.type,
        file_size_bytes: file.size,
      });
      const uploadResponse = await fetch(presign.data.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(tr("Upload failed."));
      }
      const confirmed = await axios.post(`${API}/media/confirm/${presign.data.asset_id}`);
      onChange(
        confirmed.data.url,
        confirmed.data.original_filename || file.name,
        confirmed.data.asset_id || presign.data.asset_id,
      );
      toast.success(tr("Document uploaded"));
    } catch (err) {
      const { message } = parseApiError(err, tr("Please try again."));
      toast.error(tr("Upload failed"), {
        description: message,
      });
    } finally {
      setUploading(false);
    }
  };
  if (value) {
    const isPdf = String(fileName || value).toLowerCase().includes(".pdf");
    const displayName = fileName || label;
    return (
      <div
        data-testid={`${testid}-preview`}
        className="flex flex-wrap items-center gap-3 rounded-lg border border-[#EAECEF] bg-[#FAFAFB] p-3"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-[#FF5757]">
          {isPdf ? <FileText className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[#0F1115]">{displayName}</p>
          <p className="truncate text-xs text-[#6B7280]">
            {isPdf ? tr("PDF document attached") : tr("Image attached")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => downloadDocument(value, displayName)}
          data-testid={`${testid}-download`}
          className="inline-flex h-8 items-center rounded-md border border-[#E6E8EC] px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
        >{tr("Download")}</button>
        <button
          type="button"
          onClick={() => ref.current?.click()}
          data-testid={`${testid}-replace`}
          disabled={uploading}
          className="inline-flex h-8 items-center rounded-md border border-[#E6E8EC] px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
        >
          {uploading ? tr("Uploading…") : tr("Replace")}
        </button>
        <button
          type="button"
          onClick={() => onChange(null, null)}
          data-testid={`${testid}-remove`}
          aria-label={tr("Remove file")}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#6B7280] hover:bg-[#FFEFEF] hover:text-[#9B0000]"
        >
          <X className="h-4 w-4" />
        </button>
        <input
          ref={ref}
          type="file"
          accept={ACCEPTED_DOC_MIMES.join(",")}
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
          data-testid={`${testid}-input`}
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => ref.current?.click()}
      disabled={uploading}
      data-testid={`${testid}-pick`}
      className="flex h-[88px] w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-[#CFD2D8] bg-[#FAFAFB] text-xs font-semibold text-[#6B7280] transition-colors hover:border-[#FF5757] hover:bg-[#FFF7F7] hover:text-[#9B0000]"
    >
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      {uploading ? tr("Uploading…") : tr("Click to upload")}
      <span className="text-[10px] font-medium text-[#9BA1AB]">{ACCEPTED_DOC_LABEL}</span>
      <input
        ref={ref}
        type="file"
        accept={ACCEPTED_DOC_MIMES.join(",")}
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid={`${testid}-input`}
      />
    </button>
  );
};

/* ---- Section wrapper ---- */
const Section = ({
  index,
  title,
  children,
  testid
}) => {
  usePartnerTranslation();
  return <section data-testid={testid} className="space-y-4">
    <header className="flex items-baseline gap-2 border-b border-[#F0F2F5] pb-2">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#FF5757] text-[12px] font-extrabold text-white">
        {index}
      </span>
      <h2 className="text-base font-extrabold tracking-tight text-[#0F1115] md:text-lg">
        {title}
      </h2>
    </header>
    {children}
  </section>;
};

const blank = () => ({
  legal_name: "",
  identity_registration_proof: null,
  identity_registration_proof_asset_id: null,
  identity_registration_proof_name: "",
  identity_document_type: "",
  country: "",
  city: "",
  address: "",
  primary_contact_full_name: "",
  primary_contact_email: "",
  primary_contact_phone: "+",
  emergency_contact_phone: "+",
  has_insurance: false,
  insurance_provider_name: "",
  insurance_policy_number: "",
  insurance_coverage_currency: "",
  insurance_coverage_amount: "",
  insurance_coverage_start_date: "",
  insurance_coverage_end_date: "",
  insurance_document: null,
  insurance_document_asset_id: null,
  insurance_document_name: "",
  upload_supplier_terms: false,
  supplier_terms_document: null,
  supplier_terms_document_asset_id: null,
  supplier_terms_document_name: "",
  upload_supplier_privacy: false,
  supplier_privacy_document: null,
  supplier_privacy_document_asset_id: null,
  supplier_privacy_document_name: "",
  confirm_accuracy: false,
  acknowledge_voocly_terms: false,
});

export default function LegalPage() {
  usePartnerTranslation();
  const { user, role } = useAuth();
  const showOrganizationTab = !!user;
  const [activeTab, setActiveTab] = useState(() => (user ? "details" : "terms"));
  const [data, setData] = useState(blank());
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [locationIndex, setLocationIndex] = useState(null);
  const currentDocument = useMemo(
    () => LEGAL_DOCUMENT_TABS.find((tab) => tab.key === activeTab),
    [activeTab],
  );
  const visibleErrors = useMemo(
    () => Object.entries(errors).filter(([, message]) => !!message),
    [errors],
  );
  const countryOptions = useMemo(() => toSelectOptions(COUNTRIES), []);
  const cityOptions = useMemo(() => {
    if (!data.country || !locationIndex) return [];
    return toSelectOptions(getCityNamesForCountry(data.country, locationIndex));
  }, [data.country, locationIndex]);

  useEffect(() => {
    let cancelled = false;
    loadLocationIndex().then((index) => {
      if (!cancelled) setLocationIndex(index);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* Load existing record */
  useEffect(() => {
    if (!user) {
      setLoading(false);
      setActiveTab((tab) => (tab === "details" ? "terms" : tab));
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [res, me] = await Promise.all([
          axios.get(`${API}/account/legal`),
          axios.get(`${API}/auth/me`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        const remote = res.data || {};
        const accountType = remote.account_type || me.data?.business_type || "";
        const normalisedRemote = {
          ...remote,
          primary_contact_phone: normalisePhoneInput(remote.primary_contact_phone),
          emergency_contact_phone: normalisePhoneInput(remote.emergency_contact_phone),
          upload_supplier_terms: !!(
            remote.upload_supplier_terms || remote.supplier_terms_document
          ),
          upload_supplier_privacy: !!(
            remote.upload_supplier_privacy || remote.supplier_privacy_document
          ),
        };
        if (remote.exists) {
          setData((prev) => ({
            ...prev,
            ...normalisedRemote,
            account_type: accountType,
          }));
          setLocked(true);
        } else {
          setData((prev) => ({ ...prev, account_type: accountType }));
        }
      } catch (err) {
        const { message } = parseApiError(err, tr("Please retry."));
        toast.error(tr("Couldn't load Legal info"), {
          description: message,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const set = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const setInsuranceEnabled = (enabled) => {
    setData((prev) => ({
      ...prev,
      has_insurance: enabled,
      ...(enabled
        ? {}
        : {
            insurance_provider_name: "",
            insurance_policy_number: "",
            insurance_coverage_currency: "",
            insurance_coverage_amount: "",
            insurance_coverage_start_date: "",
            insurance_coverage_end_date: "",
            insurance_document: null,
            insurance_document_asset_id: null,
            insurance_document_name: "",
          }),
    }));
    setErrors((prev) => {
      const {
        has_insurance,
        insurance_provider_name,
        insurance_policy_number,
        insurance_coverage_currency,
        insurance_coverage_amount,
        insurance_coverage_start_date,
        insurance_coverage_end_date,
        insurance_document,
        insurance_document_name,
        ...rest
      } = prev;
      return rest;
    });
  };

  const setSupplierTermsEnabled = (enabled) => {
    setData((prev) => ({
      ...prev,
      upload_supplier_terms: enabled,
      ...(enabled
        ? {}
        : {
            supplier_terms_document: null,
            supplier_terms_document_asset_id: null,
            supplier_terms_document_name: "",
          }),
    }));
    setErrors((prev) => {
      const { supplier_terms_document, supplier_terms_document_name, ...rest } = prev;
      return rest;
    });
  };

  const setSupplierPrivacyEnabled = (enabled) => {
    setData((prev) => ({
      ...prev,
      upload_supplier_privacy: enabled,
      ...(enabled
        ? {}
        : {
            supplier_privacy_document: null,
            supplier_privacy_document_asset_id: null,
            supplier_privacy_document_name: "",
          }),
    }));
    setErrors((prev) => {
      const { supplier_privacy_document, supplier_privacy_document_name, ...rest } = prev;
      return rest;
    });
  };

  const setPhone = (key, value) => {
    set(key, normalisePhoneInput(value));
  };

  const setPersonName = (key, value) => {
    set(key, normalisePersonNameInput(value));
  };

  const setInsuranceStartDate = (value) => {
    const nextValue = clampDateString(value, INSURANCE_START_MIN, INSURANCE_START_MAX);
    setData((prev) => ({
      ...prev,
      insurance_coverage_start_date: nextValue,
      insurance_coverage_end_date:
        prev.insurance_coverage_end_date && nextValue && prev.insurance_coverage_end_date < nextValue
          ? ""
          : prev.insurance_coverage_end_date,
    }));
    setErrors((prev) => {
      const { insurance_coverage_start_date, insurance_coverage_end_date, ...rest } = prev;
      return rest;
    });
  };

  const setInsuranceEndDate = (value) => {
    const min = data.insurance_coverage_start_date && data.insurance_coverage_start_date > INSURANCE_END_MIN
      ? data.insurance_coverage_start_date
      : INSURANCE_END_MIN;
    const nextValue = clampDateString(value, min, INSURANCE_END_MAX);
    set("insurance_coverage_end_date", nextValue);
    if (data.insurance_coverage_start_date && nextValue && nextValue < data.insurance_coverage_start_date) {
      setErrors((prev) => ({
        ...prev,
        insurance_coverage_end_date: "End date must be on or after the start date",
      }));
    }
  };

  const validateBeforeSubmit = () => {
    const nextErrors = {};
    if (!locked) {
      if ((data.legal_name || "").trim().length > ORG_FIELD_MAX) {
        nextErrors.legal_name = tr("Legal name must be {{value0}} characters or less", { value0: ORG_FIELD_MAX });
      }
      if (!PHONE_PATTERN.test(data.primary_contact_phone || "")) {
        nextErrors.primary_contact_phone = LEGAL_ERROR_MESSAGES.primary_contact_phone;
      }
      if (!PHONE_PATTERN.test(data.emergency_contact_phone || "")) {
        nextErrors.emergency_contact_phone = LEGAL_ERROR_MESSAGES.emergency_contact_phone;
      }
    }
    if (data.has_insurance) {
      if ((data.insurance_provider_name || "").trim().length > INSURANCE_FIELD_MAX) {
        nextErrors.insurance_provider_name = tr("Insurance provider must be {{value0}} characters or less", { value0: INSURANCE_FIELD_MAX });
      }
      if ((data.insurance_policy_number || "").trim().length > INSURANCE_FIELD_MAX) {
        nextErrors.insurance_policy_number = tr("Policy number must be {{value0}} characters or less", { value0: INSURANCE_FIELD_MAX });
      }
      if (String(data.insurance_coverage_amount || "").trim().length > INSURANCE_FIELD_MAX) {
        nextErrors.insurance_coverage_amount = tr("Coverage amount must be {{value0}} characters or less", { value0: INSURANCE_FIELD_MAX });
      }
      if (
        data.insurance_coverage_start_date &&
        (data.insurance_coverage_start_date < INSURANCE_START_MIN ||
          data.insurance_coverage_start_date > INSURANCE_START_MAX)
      ) {
        nextErrors.insurance_coverage_start_date =
          tr("Coverage start date must be within the last 5 years and no more than 1 year ahead");
      }
      if (
        data.insurance_coverage_end_date &&
        (data.insurance_coverage_end_date < INSURANCE_END_MIN ||
          data.insurance_coverage_end_date > INSURANCE_END_MAX)
      ) {
        nextErrors.insurance_coverage_end_date =
          tr("Coverage end date must be today or later and no more than 5 years ahead");
      }
    }
    if (
      data.has_insurance &&
      data.insurance_coverage_start_date &&
      data.insurance_coverage_end_date &&
      data.insurance_coverage_end_date < data.insurance_coverage_start_date
    ) {
      nextErrors.insurance_coverage_end_date = tr("End date must be on or after the start date");
    }
    if (Object.keys(nextErrors).length) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      toast.error(tr("Please fix the highlighted fields"));
      return false;
    }
    return true;
  };

  const buildSubmitPayload = () => {
    const payload = {
      ...data,
      has_insurance: !!data.has_insurance,
      upload_supplier_terms: !!data.upload_supplier_terms,
      upload_supplier_privacy: !!data.upload_supplier_privacy,
    };
    if (!payload.has_insurance) {
      payload.insurance_provider_name = "";
      payload.insurance_policy_number = "";
      payload.insurance_coverage_currency = "";
      payload.insurance_coverage_amount = "";
      payload.insurance_coverage_start_date = "";
      payload.insurance_coverage_end_date = "";
      payload.insurance_document = null;
      payload.insurance_document_asset_id = null;
      payload.insurance_document_name = "";
    }
    if (!payload.upload_supplier_terms) {
      payload.supplier_terms_document = null;
      payload.supplier_terms_document_asset_id = null;
      payload.supplier_terms_document_name = "";
    }
    if (!payload.upload_supplier_privacy) {
      payload.supplier_privacy_document = null;
      payload.supplier_privacy_document_asset_id = null;
      payload.supplier_privacy_document_name = "";
    }
    return payload;
  };

  /* ---- Submit ---- */
  const submit = async () => {
    if (!validateBeforeSubmit()) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/account/legal`, buildSubmitPayload());
      if (res.data?.success) {
        toast.success(
          locked ? tr("Insurance & policies updated") : tr("Legal info saved"),
          {
            description: locked
              ? "Your changes have been saved."
              : "You can now publish products on Voocly.",
          },
        );
        setLocked(true);
        // Refetch to sync any server-side normalisations
        const r = await axios.get(`${API}/account/legal`);
        const refreshed = r.data || {};
        setData((prev) => ({
          ...prev,
          ...refreshed,
          primary_contact_phone: normalisePhoneInput(refreshed.primary_contact_phone),
          emergency_contact_phone: normalisePhoneInput(refreshed.emergency_contact_phone),
          upload_supplier_terms: !!(
            refreshed.upload_supplier_terms || refreshed.supplier_terms_document
          ),
          upload_supplier_privacy: !!(
            refreshed.upload_supplier_privacy || refreshed.supplier_privacy_document
          ),
        }));
        setErrors({});
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.errors) {
        const map = {};
        detail.errors.forEach((k) => {
          map[k] = LEGAL_ERROR_MESSAGES[k] || "Please review this field";
        });
        setErrors(map);
        const firstMessage = map[detail.errors[0]];
        toast.error(tr("Please fix the highlighted fields"), {
          description: firstMessage,
        });
      } else {
        const { message } = parseApiError(err, tr("Please review and try again."));
        toast.error(tr("Couldn't save"), {
          description: message,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <main className="mx-auto flex w-full max-w-[820px] items-center justify-center px-4 py-20 text-[#6B7280]">
        <Loader2 className="me-3 h-5 w-5 animate-spin" />{tr("Loading Legal info…")}</main>
    );
  }

  const lockedField = (key) => {
    const adminCanEditContact = role === "administration";
    if (
      adminCanEditContact &&
      [
        "primary_contact_full_name",
        "primary_contact_email",
        "primary_contact_phone",
        "emergency_contact_phone",
      ].includes(key)
    ) {
      return false;
    }
    return locked && [
      "legal_name",
      "country",
      "city",
      "address",
      "primary_contact_full_name",
      "primary_contact_email",
      "primary_contact_phone",
      "emergency_contact_phone",
    ].includes(key);
  };
  return (
    <main
      data-testid="legal-page"
      className="mx-auto w-full max-w-[1100px] flex-1 px-4 py-8 sm:px-6 md:px-10"
    >
      <header className="pb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0F1115] md:text-4xl">{tr("Partner legal center")}</h1>
        {user && !locked && (
          <p className="mt-2 max-w-prose text-sm text-[#6B7280]">{tr("We need a few details to onboard you as a partner. Once submitted, your ")}<strong>{tr("required organization details")}</strong>{tr(" are locked. Optional organization details, insurance, and policy documents remain editable.")}</p>
        )}
      </header>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <nav
            data-testid="legal-tabs"
            className="rounded-xl border border-[#EAECEF] bg-white p-2"
            aria-label={tr("Legal tabs")}
          >
            {showOrganizationTab && (
              <button
                type="button"
                data-testid="legal-tab-details"
                onClick={() => setActiveTab("details")}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-start text-sm font-semibold transition-colors",
                  activeTab === "details"
                    ? "bg-[#FFF1F1] text-[#0F1115]"
                    : "text-[#6B7280] hover:bg-[#FAFAFB] hover:text-[#0F1115]",
                )}
              >
                <CheckCircle2
                  className={cn(
                    "h-4 w-4 flex-shrink-0",
                    activeTab === "details" ? "text-[#FF5757]" : "text-[#9BA1AB]",
                  )}
                  strokeWidth={1.9}
                />
                <span>{tr("Your organization")}</span>
              </button>
            )}
            {LEGAL_DOCUMENT_TABS.map((tab) => {
              const TabIcon = tab.icon;
              const selected = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  data-testid={`legal-tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-start text-sm font-semibold transition-colors",
                    selected
                      ? "bg-[#FFF1F1] text-[#0F1115]"
                      : "text-[#6B7280] hover:bg-[#FAFAFB] hover:text-[#0F1115]",
                  )}
                >
                  <TabIcon
                    className={cn(
                      "h-4 w-4 flex-shrink-0",
                      selected ? "text-[#FF5757]" : "text-[#9BA1AB]",
                    )}
                    strokeWidth={1.9}
                  />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="min-w-0">
          {activeTab === "details" ? (
            <>
      {locked && (
        <div
          data-testid="legal-locked-banner"
          className="mb-6 flex items-start gap-3 rounded-lg border border-[#D1FAD5] bg-[#F0FFF1] px-4 py-3"
        >
          <CheckCircle2
            className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#0F6B2B]"
            strokeWidth={2}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#0F6B2B]">{tr("Your Legal information is on file.")}</p>
            <p className="mt-0.5 text-xs text-[#0F6B2B]/85">{tr("Optional organization details, insurance, and partner policy documents remain editable. To change locked company info,")}{" "}
              <a
                href="/contact"
                data-testid="legal-contact-support"
                className="underline-offset-2 hover:underline"
              >{tr("contact Voocly support")}</a>
              .
            </p>
          </div>
        </div>
      )}

      <div className="space-y-10 rounded-2xl border border-[#EAECEF] bg-white p-5 sm:p-8">
        {/* SECTION 1 */}
        <Section index={1} title={tr("Organization details")} testid="legal-section-org">
          <FieldRow
            label={tr("Legal name")}
            required
            helper={tr("Official company, institution, club, or individual legal name")}
            error={errors.legal_name}
            testid="field-legal-name"
          >
            <LockedTextInput
              locked={lockedField("legal_name")}
              value={data.legal_name}
              onChange={(v) => set("legal_name", limitText(v, ORG_FIELD_MAX))}
              placeholder={tr("e.g. Voocly Travel Co. Ltd")}
              testid="legal-name-input"
              maxLength={ORG_FIELD_MAX}
              error={errors.legal_name}
            />
          </FieldRow>
          <FieldRow
            label={tr("Identity/registration proof")}
            helper={tr("Optional. {{value0}}", { value0: identityProofHelper(data.account_type) })}
            error={errors.identity_registration_proof}
            testid="field-identity-proof"
          >
            <DocumentUpload
              value={data.identity_registration_proof}
              fileName={data.identity_registration_proof_name}
              assetType="legal_identity_proof"
              onChange={(url, name, assetId) => {
                set("identity_registration_proof", url);
                set("identity_registration_proof_asset_id", assetId || null);
                set("identity_registration_proof_name", name || "");
              }}
              testid="legal-identity-proof"
              label={tr("Identity/registration proof")}
            />
          </FieldRow>
          <FieldRow
            label={tr("Please describe the document type")}
            helper={tr("{{value0}}/60 characters", { value0: (data.identity_document_type || "").length })}
            error={errors.identity_document_type}
            testid="field-identity-document-type"
          >
            <TextInput
              value={data.identity_document_type}
              onChange={(v) => set("identity_document_type", v.slice(0, 60))}
              placeholder={tr("e.g. Certificate of Incorporation")}
              testid="legal-identity-document-type-input"
              maxLength={60}
              error={errors.identity_document_type}
            />
          </FieldRow>
          <div className="grid gap-4 md:grid-cols-2">
            <FieldRow
              label={tr("Country")}
              required
              error={errors.country}
              testid="field-country"
            >
              <LockedSelect
                locked={lockedField("country")}
                value={data.country}
                onChange={(v) => {
                  const nextCities = locationIndex
                    ? getCityNamesForCountry(v, locationIndex)
                    : [];
                  setData((prev) => ({
                    ...prev,
                    country: v,
                    city: nextCities.includes(prev.city) ? prev.city : "",
                  }));
                  setErrors((prev) => {
                    const { country, city, ...rest } = prev;
                    return rest;
                  });
                }}
                options={countryOptions}
                placeholder={tr("Select country")}
                testid="legal-country-input"
                error={errors.country}
              />
            </FieldRow>
            <FieldRow
              label={tr("City")}
              required
              helper={!data.country ? tr("Select a country first") : undefined}
              error={errors.city}
              testid="field-city"
            >
              <LockedSelect
                locked={lockedField("city")}
                value={data.city}
                onChange={(v) => set("city", v)}
                options={cityOptions}
                placeholder={
                  data.country
                    ? locationIndex
                      ? tr("Select city")
                      : tr("Loading cities…")
                    : tr("Select country first")
                }
                testid="legal-city-input"
                error={errors.city}
                searchable
                virtualized
                searchPlaceholder={tr("Search city")}
              />
            </FieldRow>
          </div>
          <FieldRow
            label={tr("Address")}
            required
            helper={tr("Enter your street address or business address")}
            error={errors.address}
            testid="field-address"
          >
            <LockedTextInput
              locked={lockedField("address")}
              value={data.address}
              onChange={(v) => set("address", v)}
              placeholder={tr("Street address")}
              testid="legal-address-input"
              error={errors.address}
            />
          </FieldRow>
        </Section>

        {/* SECTION 2 */}
        <Section index={2} title={tr("Contact details")} testid="legal-section-contact">
          <div className="grid gap-4 md:grid-cols-2">
            <FieldRow
              label={tr("Main contact person")}
              required
              error={errors.primary_contact_full_name}
              testid="field-contact-name"
            >
              <LockedTextInput
                locked={lockedField("primary_contact_full_name")}
                value={data.primary_contact_full_name}
                onChange={(v) => setPersonName("primary_contact_full_name", v)}
                placeholder={tr("Full name")}
                testid="legal-contact-name-input"
                error={errors.primary_contact_full_name}
              />
            </FieldRow>
            <FieldRow
              label={tr("Contact email")}
              required
              error={errors.primary_contact_email}
              testid="field-contact-email"
            >
              <LockedTextInput
                locked={lockedField("primary_contact_email")}
                value={data.primary_contact_email}
                onChange={(v) => set("primary_contact_email", v)}
                placeholder="hello@yourcompany.com"
                testid="legal-contact-email-input"
                type="email"
                error={errors.primary_contact_email}
              />
            </FieldRow>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <FieldRow
              label={tr("Contact phone")}
              required
              error={errors.primary_contact_phone}
              testid="field-contact-phone"
            >
              <LockedTextInput
                locked={lockedField("primary_contact_phone")}
                value={data.primary_contact_phone}
                onChange={(v) => setPhone("primary_contact_phone", v)}
                placeholder="+442000000000"
                testid="legal-contact-phone-input"
                inputMode="numeric"
                pattern="\\+\\d+"
                maxLength={PHONE_DIGIT_MAX + 1}
                error={errors.primary_contact_phone}
              />
            </FieldRow>
            <FieldRow
              label={tr("Emergency contact phone")}
              required
              helper={tr("Number to reach in case of urgent guest or safety issues")}
              error={errors.emergency_contact_phone}
              testid="field-emergency-phone"
            >
              <LockedTextInput
                locked={lockedField("emergency_contact_phone")}
                value={data.emergency_contact_phone}
                onChange={(v) => setPhone("emergency_contact_phone", v)}
                placeholder="+447000000000"
                testid="legal-emergency-phone-input"
                inputMode="numeric"
                pattern="\\+\\d+"
                maxLength={PHONE_DIGIT_MAX + 1}
                error={errors.emergency_contact_phone}
              />
            </FieldRow>
          </div>
        </Section>

        {/* SECTION 3 */}
        <Section
          index={3}
          title={tr("Insurance (if applicable)")}
          testid="legal-section-insurance"
        >
          <CheckboxField
            value={!!data.has_insurance}
            onChange={setInsuranceEnabled}
            label={tr("This organization holds liability or other relevant insurance for its activities")}
            testid="legal-has-insurance"
          />
          {data.has_insurance && (
            <div
              data-testid="legal-insurance-fields"
              className="space-y-4 rounded-lg border border-[#F0F2F5] bg-[#FAFAFB] p-4"
            >
              <div className="grid gap-4 md:grid-cols-2">
                <FieldRow
                  label={tr("Insurance provider")}
                  required
                  error={errors.insurance_provider_name}
                  testid="field-ins-provider"
                >
                  <TextInput
                    value={data.insurance_provider_name}
                    onChange={(v) => set("insurance_provider_name", limitText(v, INSURANCE_FIELD_MAX))}
                    placeholder={tr("e.g. Lloyd's of London")}
                    testid="legal-ins-provider-input"
                    maxLength={INSURANCE_FIELD_MAX}
                    error={errors.insurance_provider_name}
                  />
                </FieldRow>
                <FieldRow
                  label={tr("Policy number")}
                  required
                  error={errors.insurance_policy_number}
                  testid="field-ins-policy"
                >
                  <TextInput
                    value={data.insurance_policy_number}
                    onChange={(v) => set("insurance_policy_number", limitText(v, INSURANCE_FIELD_MAX))}
                    placeholder={tr("e.g. POL-12345")}
                    testid="legal-ins-policy-input"
                    maxLength={INSURANCE_FIELD_MAX}
                    error={errors.insurance_policy_number}
                  />
                </FieldRow>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FieldRow
                  label={tr("Coverage currency")}
                  required
                  error={errors.insurance_coverage_currency}
                  testid="field-ins-currency"
                >
                  <SelectField
                    value={data.insurance_coverage_currency}
                    onChange={(v) => set("insurance_coverage_currency", v)}
                    options={TICKET_CURRENCIES}
                    placeholder={tr("Pick currency")}
                    testid="legal-ins-currency-select"
                    error={errors.insurance_coverage_currency}
                  />
                </FieldRow>
                <FieldRow
                  label={tr("Coverage amount")}
                  required
                  helper={tr("Total coverage limit in selected currency")}
                  error={errors.insurance_coverage_amount}
                  testid="field-ins-amount"
                >
                  <TextInput
                    type="number"
                    min={0}
                    value={data.insurance_coverage_amount}
                    onChange={(v) => set("insurance_coverage_amount", limitText(v, INSURANCE_FIELD_MAX))}
                    placeholder={tr("e.g. 1000000")}
                    testid="legal-ins-amount-input"
                    maxLength={INSURANCE_FIELD_MAX}
                    error={errors.insurance_coverage_amount}
                  />
                </FieldRow>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FieldRow
                  label={tr("Coverage start date")}
                  required
                  error={errors.insurance_coverage_start_date}
                  testid="field-ins-start"
                >
                  <TextInput
                    type="date"
                    value={data.insurance_coverage_start_date}
                    onChange={setInsuranceStartDate}
                    min={INSURANCE_START_MIN}
                    max={INSURANCE_START_MAX}
                    testid="legal-ins-start-input"
                    error={errors.insurance_coverage_start_date}
                  />
                </FieldRow>
                <FieldRow
                  label={tr("Coverage end date")}
                  required
                  error={errors.insurance_coverage_end_date}
                  testid="field-ins-end"
                >
                  <TextInput
                    type="date"
                    value={data.insurance_coverage_end_date}
                    onChange={setInsuranceEndDate}
                    min={
                      data.insurance_coverage_start_date &&
                      data.insurance_coverage_start_date > INSURANCE_END_MIN
                        ? data.insurance_coverage_start_date
                        : INSURANCE_END_MIN
                    }
                    max={INSURANCE_END_MAX}
                    testid="legal-ins-end-input"
                    error={errors.insurance_coverage_end_date}
                  />
                </FieldRow>
              </div>
              <FieldRow
                label={tr("Insurance document")}
                required
                helper={tr("Upload the current insurance certificate or policy document")}
                error={errors.insurance_document}
                testid="field-ins-document"
              >
                <DocumentUpload
                  value={data.insurance_document}
                  fileName={data.insurance_document_name}
                  assetType="legal_insurance_document"
                  onChange={(url, name, assetId) => {
                    set("insurance_document", url);
                    set("insurance_document_asset_id", assetId || null);
                    set("insurance_document_name", name || "");
                  }}
                  testid="legal-ins-document"
                  label={tr("Insurance document")}
                />
              </FieldRow>
            </div>
          )}
        </Section>

        {/* SECTION 4 */}
        <Section
          index={4}
          title={tr("Partner policies (optional)")}
          testid="legal-section-policies"
        >
          <div className="space-y-5">
            <div className="rounded-xl border border-[#EAECEF] bg-[#FAFAFB] p-4">
              <CheckboxField
                value={!!data.upload_supplier_terms}
                onChange={setSupplierTermsEnabled}
                label={tr("I'd like to upload my organization's terms & conditions.")}
                testid="legal-upload-terms-toggle"
              />
              {data.upload_supplier_terms && (
                <div className="mt-4">
                  <FieldRow
                    label={tr("Partner terms and conditions")}
                    required
                    helper={tr("Upload your own terms (PDF, JPG or PNG)")}
                    error={errors.supplier_terms_document}
                    testid="field-supplier-terms"
                  >
                    <DocumentUpload
                      value={data.supplier_terms_document}
                      fileName={data.supplier_terms_document_name}
                      assetType="legal_supplier_terms"
                      onChange={(url, name, assetId) => {
                        set("supplier_terms_document", url);
                        set("supplier_terms_document_asset_id", assetId || null);
                        set("supplier_terms_document_name", name || "");
                      }}
                      testid="legal-terms-doc"
                      label={tr("Partner T&C")}
                    />
                  </FieldRow>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[#EAECEF] bg-[#FAFAFB] p-4">
              <CheckboxField
                value={!!data.upload_supplier_privacy}
                onChange={setSupplierPrivacyEnabled}
                label={tr("I'd like to upload my organization's privacy policy.")}
                testid="legal-upload-privacy-toggle"
              />
              {data.upload_supplier_privacy && (
                <div className="mt-4">
                  <FieldRow
                    label={tr("Partner privacy policy")}
                    required
                    helper={tr("Upload your own privacy policy (PDF, JPG or PNG)")}
                    error={errors.supplier_privacy_document}
                    testid="field-supplier-privacy"
                  >
                    <DocumentUpload
                      value={data.supplier_privacy_document}
                      fileName={data.supplier_privacy_document_name}
                      assetType="legal_supplier_privacy"
                      onChange={(url, name, assetId) => {
                        set("supplier_privacy_document", url);
                        set("supplier_privacy_document_asset_id", assetId || null);
                        set("supplier_privacy_document_name", name || "");
                      }}
                      testid="legal-privacy-doc"
                      label={tr("Partner privacy policy")}
                    />
                  </FieldRow>
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* SECTION 5 */}
        <Section
          index={5}
          title={tr("Confirmation")}
          testid="legal-section-confirmation"
        >
          <CheckboxField
            value={!!data.confirm_accuracy}
            onChange={(v) => set("confirm_accuracy", v)}
            label={
              <>{tr("I confirm that the information provided in this Legal form is true, accurate, and complete. ")}<span className="text-[#9B0000]">*</span>
              </>
            }
            testid="legal-confirm-accuracy"
            error={errors.confirm_accuracy}
          />
          <CheckboxField
            value={!!data.acknowledge_voocly_terms}
            onChange={(v) => set("acknowledge_voocly_terms", v)}
            label={
              <>{tr("I understand that Voocly's Terms & Conditions and Privacy Policy govern the use of the platform and take precedence over any partner terms or policies in case of conflict.")}{" "}
                <span className="text-[#9B0000]">*</span>
              </>
            }
            testid="legal-acknowledge-voocly-terms"
            error={errors.acknowledge_voocly_terms}
          />
          <p
            data-testid="legal-ui-note"
            className="rounded-md bg-[#F3EEEE] p-3 text-xs leading-relaxed text-[#6B7280]"
          >{tr("Once this Legal form is submitted, required organization details are locked. To change them later, please")}{" "}
            <a
              href="/contact"
              className="font-semibold text-[#0F1115] underline-offset-2 hover:underline"
            >{tr("contact Voocly support")}</a>{tr(". You'll still be able to update optional organization details, insurance, and policy documents anytime.")}</p>
        </Section>
      </div>

      <footer className="sticky bottom-0 mt-6 flex flex-wrap items-center justify-end gap-2 rounded-2xl border border-[#EAECEF] bg-white/95 p-4 shadow-[0_-2px_24px_-12px_rgba(15,17,21,0.18)] backdrop-blur">
        {visibleErrors.length > 0 && (
          <div
            data-testid="legal-error-summary"
            className="me-auto max-w-full rounded-lg border border-[#F3C7C7] bg-[#FFF7F7] px-3 py-2 text-xs font-semibold text-[#9B0000] md:max-w-[520px]"
          >{tr("Please fix: ")}{visibleErrors.map(([, message]) => message).join("; ")}
          </div>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="legal-submit"
          className="humrahe-btn-primary inline-flex h-10 items-center gap-1.5 rounded-md px-5 text-sm font-semibold disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {locked ? tr("Save changes") : tr("Submit & unlock publishing")}
        </button>
      </footer>
            </>
          ) : (
            <LegalDocumentPanel document={currentDocument} />
          )}
        </div>
      </section>
    </main>
  );
}

const LegalDocumentPanel = ({ document }) => {
  usePartnerTranslation();
  if (!document) return null;
  return (
    <article
      data-testid={`legal-doc-content-${document.key}`}
      className="rounded-xl border border-[#EAECEF] bg-white"
    >
      <div className="border-b border-[#EAECEF] px-5 py-5 sm:px-6">
        <h2 className="text-xl font-extrabold tracking-tight text-[#0F1115]">
          {document.label}
        </h2>
        <p className="mt-1 text-xs font-semibold text-[#9BA1AB]">
          {UPDATED_AT}
        </p>
        <p className="mt-4 text-sm leading-6 text-[#6B7280]">
          {document.intro}
        </p>
      </div>

      <div className="space-y-5 px-5 py-5 sm:px-6">
        {document.sections.map((section) => (
          <section key={section.title}>
            <h3 className="text-sm font-extrabold text-[#0F1115]">
              {section.title}
            </h3>
            <div className="mt-2 space-y-2">
              {(Array.isArray(section.body) ? section.body : [section.body]).map((paragraph) => (
                <p key={paragraph} className="text-sm leading-6 text-[#5b5f66]">
                  {paragraph}
                </p>
              ))}
            </div>
          </section>
        ))}
      </div>

      {!document.reviewed && (
        <div className="border-t border-[#EAECEF] bg-[#FAFAFB] px-5 py-4 sm:px-6">
          <p className="text-xs leading-5 text-[#6B7280]">{tr("This placeholder document is for product and UX review only. It should be replaced with reviewed legal copy before production use.")}</p>
        </div>
      )}
    </article>
  );
};

/* ---- Locked-aware wrappers around the existing field primitives ---- */
const LockedTextInput = ({
  locked,
  ...props
}) => {
  usePartnerTranslation();
  return locked ? <LockedDisplay value={props.value} testid={props.testid} /> : <TextInput {...props} />;
};

const LockedTextArea = ({
  locked,
  ...props
}) => {
  usePartnerTranslation();
  return locked ? <LockedDisplay value={props.value} testid={props.testid} multiline /> : <TextArea {...props} />;
};

const LockedSelect = ({
  locked,
  ...props
}) => {
  usePartnerTranslation();
  return locked ? <LockedDisplay value={props.value} testid={props.testid} /> : <SelectField {...props} />;
};

const LockedDisplay = ({
  value,
  testid,
  multiline = false
}) => {
  usePartnerTranslation();
  return <div data-testid={testid} className={cn("flex items-start justify-between gap-2 rounded-md border border-[#EAECEF] bg-[#FAFAFB] px-3 py-2 text-sm text-[#0F1115]", multiline ? "min-h-[44px]" : "h-11 items-center")}>
    <span className="min-w-0 flex-1 break-words">
      {value ? value : <em className="text-[#9BA1AB]">—</em>}
    </span>
    <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#9BA1AB]" strokeWidth={1.8} />
  </div>;
};
